import { useMemo } from "react";
import { useCurrentUserRoles } from "../../api/account";
import { IdentityRoles } from "../../configure/security/IdentityRoles";
import { useCurrentUserRolesCache } from "../../globalState/currentUserDetails/useCurrentUserRolesCache";

/**
 * Roles that run campaigns and so get the full help content. Users without any of these only answer questionnaires,
 * so they only get the help written for delegates.
 */
export const campaignAdminRoles = [
    IdentityRoles.Administration,
    IdentityRoles.SubscriptionAdministration,
    IdentityRoles.SubscriptionDepartmentAdministration,
];

/**
 * Returns true if the current user runs campaigns, false if they only answer questionnaires, or undefined while their roles are still loading.
 */
export function useIsCampaignAdmin(): boolean | undefined {
    // Make sure the roles get loaded.
    const { data: { roles } } = useCurrentUserRoles();

    // The roles cache starts with an empty list of roles before anything has loaded, which would look like a user with
    // no admin roles, so also check the cache to know the roles have really been loaded for the signed in user.
    const [rolesCache] = useCurrentUserRolesCache();
    const hasLoadedRoles = rolesCache.isAuthenticated && !rolesCache.isLoading;

    return useMemo(() => {
        if (!hasLoadedRoles || !roles) {
            return undefined;
        }

        return roles.some(role => campaignAdminRoles.includes(role));
    }, [roles, hasLoadedRoles]);
}
